import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import { useToast } from '@radiology/ui';
import { MODULE_LABELS, ROLES_BY_MODULE, useSetRole, type AdminUser, type ModuleKey } from './api';
import {
  roleIsScoped,
  scopeNeedsSection,
  useAddScope,
  useLookup,
  useRemoveScope,
  useRoleScopes,
} from './scopes';

/**
 * Roles, and where each one applies.
 *
 * This mirrors the legacy certifications admin form, which Phase 4 did not:
 * several roles at once, and for Supervisor and Manager a hospital+section per
 * assignment — with more than one assignment allowed per role, so a supervisor
 * can cover two sections or two hospitals. `alshehrim18` in the migrated data
 * covers Ultrasound at both KAMC and KASCH.
 *
 * A role with no assignment falls back to the person's profile hospital and
 * section, which is what every grant did before `core.role_scopes` existed. That
 * is stated in the UI rather than left implicit, because "no rows" and "the
 * whole hospital" would otherwise look the same.
 */
export function RoleAssignments({
  user,
  adminModules,
  onError,
}: {
  user: AdminUser;
  adminModules: ModuleKey[];
  onError: (message: string) => void;
}) {
  const scopes = useRoleScopes();
  const setRole = useSetRole();
  const addScope = useAddScope();
  const removeScope = useRemoveScope();
  const toast = useToast();

  const hospitals = useLookup('hospital');
  const sections = useLookup('section');

  const [adding, setAdding] = useState<{ module: ModuleKey; role: string } | null>(null);
  // Lists, not single values: AC-04 and AC-05 ask for multi-select on both, and
  // the CR's example means the cross product of the two.
  const [hospital, setHospital] = useState<string[]>([]);
  const [section, setSection] = useState<string[]>([]);

  const scopesFor = (module: ModuleKey, role: string) =>
    (scopes.data ?? []).filter(
      s => s.user_id === user.user_id && s.module === module && s.role === role,
    );

  const toggleRole = (module: ModuleKey, role: string, held: boolean) => {
    setRole.mutate(
      { userId: user.user_id, module, role, grant: !held },
      {
        onSuccess: () => toast.success(held ? `Removed ${role}` : `Granted ${role}`),
        onError: (e: Error) => onError(e.message),
      },
    );
  };

  const submitScope = () => {
    if (!adding) return;
    const hospitals = hospital.map(h => h.trim()).filter(Boolean);
    const sections = section.map(x => x.trim()).filter(Boolean);

    // BR-07: at least one hospital AND one section. Enforced here so the
    // administrator is told, and in the database by failing closed — a Manager
    // with no assignment sees nothing rather than falling back to their profile.
    if (hospitals.length === 0) {
      onError('Choose at least one hospital.');
      return;
    }
    if (scopeNeedsSection(adding.role) && sections.length === 0) {
      onError('Choose at least one section — a manager must be assigned both (BR-07).');
      return;
    }

    addScope.mutate(
      { userId: user.user_id, module: adding.module, role: adding.role, hospitals, sections },
      {
        onSuccess: () => {
          const n = hospitals.length * Math.max(sections.length, 1);
          toast.success(n === 1 ? 'Assignment added' : `${n} assignments added`);
          setAdding(null);
          setHospital([]);
          setSection([]);
        },
        onError: (e: Error) => onError(e.message),
      },
    );
  };

  return (
    <Stack gap={1.5}>
      <Typography variant="overline" color="text.secondary">
        Roles and assignments
      </Typography>

      {(Object.keys(ROLES_BY_MODULE) as ModuleKey[]).map(module => {
        const held = user.roles[module] ?? [];
        const editable = adminModules.includes(module);

        return (
          <Stack key={module} gap={0.75}>
            <Stack direction="row" gap={1} alignItems="baseline">
              <Typography variant="body2">{MODULE_LABELS[module]}</Typography>
              {!editable && (
                <Typography variant="caption" color="text.secondary">
                  you do not administer this module
                </Typography>
              )}
            </Stack>

            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {ROLES_BY_MODULE[module].map(role => {
                const on = held.includes(role);
                const chip = (
                  <Chip
                    key={role}
                    size="small"
                    label={role}
                    color={on ? 'primary' : undefined}
                    variant={on ? 'filled' : 'outlined'}
                    onClick={editable ? () => toggleRole(module, role, on) : undefined}
                    sx={{ opacity: editable ? 1 : 0.5 }}
                  />
                );
                return editable ? (
                  chip
                ) : (
                  <Tooltip key={role} title="Only an admin of this module can change it">
                    <span>{chip}</span>
                  </Tooltip>
                );
              })}
            </Stack>

            {/* Assignments, for the roles that carry their own scope. */}
            {held
              .filter(role => roleIsScoped(module, role))
              .map(role => {
                const assigned = scopesFor(module, role);
                return (
                  <Stack key={`${module}-${role}`} gap={0.5} sx={{ pl: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {role} covers
                    </Typography>

                    {assigned.length === 0 ? (
                      <Typography variant="caption" color="warning.main">
                        nothing assigned — falls back to their profile
                        {user.hospital ? ` (${user.hospital}` : ' (no hospital'}
                        {user.section ? ` / ${user.section})` : ')'}
                      </Typography>
                    ) : (
                      <Stack direction="row" gap={0.75} flexWrap="wrap">
                        {assigned.map(s => (
                          <Chip
                            key={s.id}
                            size="small"
                            variant="outlined"
                            label={
                              s.section
                                ? `${s.hospital} / ${s.section}`
                                : `${s.hospital} — all sections`
                            }
                            onDelete={
                              editable
                                ? () =>
                                    removeScope.mutate(s.id, {
                                      onError: (e: Error) => onError(e.message),
                                    })
                                : undefined
                            }
                          />
                        ))}
                      </Stack>
                    )}

                    {editable &&
                      (adding?.module === module && adding.role === role ? (
                        <Stack direction="row" gap={1} alignItems="flex-start" flexWrap="wrap">
                          <Autocomplete
                            multiple
                            freeSolo
                            size="small"
                            options={hospitals.data ?? []}
                            value={hospital}
                            onChange={(_e, v) => setHospital(v)}
                            sx={{ minWidth: 260 }}
                            renderInput={p => <TextField {...p} label="Hospitals" />}
                          />
                          {scopeNeedsSection(role) && (
                            <Autocomplete
                              multiple
                              freeSolo
                              size="small"
                              options={sections.data ?? []}
                              value={section}
                              onChange={(_e, v) => setSection(v)}
                              sx={{ minWidth: 300 }}
                              renderInput={p => <TextField {...p} label="Sections" />}
                            />
                          )}
                          <Button size="small" variant="contained" onClick={submitScope}>
                            Add
                          </Button>
                          <Button size="small" color="inherit" onClick={() => setAdding(null)}>
                            Cancel
                          </Button>
                        </Stack>
                      ) : (
                        <Button
                          size="small"
                          sx={{ alignSelf: 'flex-start' }}
                          onClick={() => {
                            setAdding({ module, role });
                            setHospital(user.hospital ? [user.hospital] : []);
                            setSection(
                              scopeNeedsSection(role) && user.section ? [user.section] : [],
                            );
                          }}
                        >
                          Add an assignment
                        </Button>
                      ))}
                  </Stack>
                );
              })}
          </Stack>
        );
      })}

      <Alert severity="info">
        A supervisor or manager with no assignment is scoped by their profile instead. Assignments
        let one person cover more than one section or hospital — which is what the old
        certifications admin screen did, and what four supervisors in the migrated data actually
        need.
        <br />
        Assignments are <strong>hospital and section</strong>, because that is what a certificate
        carries. Department is a directory field on the person and scopes nothing (decision #13).
        <br />
        Picking several of each creates every combination: two hospitals and two sections give four
        assignments. An <strong>Operation manager</strong> has none — that role is unrestricted.
      </Alert>
    </Stack>
  );
}
